import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BorderPicker } from 'components/MarkupDetails/ColorPicker'
import ModelName from './modelName'
import DropLengthInput from 'components/NetworkClosetDetails/DropLengthInput'
import WiringPanel from 'components/WiringPanel'
import Panel from 'components/Panel'
import ConfigurationSummary from 'components/DoorEntityConfigurationSummary'
import DeviceDescription from 'components/DeviceDescription'
import EthernetExpansionWarning from './ethernetExpansionWarning'
import WireDistanceWarning from 'components/Wire/DistanceWarning'
import { updateUiAction } from 'actions/uiActions'
import { updateEntityAction, destroyDoorControllerAction, addDoorAction } from 'actions/entityActions'
import { updateCurrentEntityAction, checkOfflineLimitsAction } from 'actions/applicationActions'
import { zoomToDoorControllerAction } from 'actions/mapActions'
import { messageAction } from 'actions/messageActions'
import { currentDoorControllerSelector, connectedDoorsSelector, connectedExpansionsSelector,
  doorControllerModelCountSelector, doorControllerShowAddFirstDoorSelector } from 'selectors/components'
import getNewDeviceLocation from 'helpers/accessControl/getNewDeviceLocation'

const mapStateToProps = state => {
  const { map: { ref }, ui: { wiringPanelOpen, doorControllerSummaryPanelOpen, cameraDescriptionPanelOpen, isImperial } } = state
  const { connectedDoors, totalDistance } = connectedDoorsSelector(state)
  const { expansions, totalExpansionDistance } = connectedExpansionsSelector(state)

  return {
    currentDoorController: currentDoorControllerSelector(state),
    map: ref,
    doors: connectedDoors,
    totalDistance,
    expansions,
    totalExpansionDistance,
    wiringPanelOpen,
    doorControllerSummaryPanelOpen,
    cameraDescriptionPanelOpen,
    isImperial,
    modelCount: doorControllerModelCountSelector(state),
    showAddFirstDoor: doorControllerShowAddFirstDoorSelector(state)
  }
}

class DoorControllerDetails extends React.Component {
  constructor(props) {
    super(props)
    this.markerDragListeners = []
    this.state = { dragging: false }
  }

  componentDidMount = _ => {
    const { currentDoorController: { fillMarker } } = this.props
    if (fillMarker) this.addDragListeners(fillMarker)
  }

  componentDidUpdate = prevProps => {
    const { currentDoorController: { fillMarker } } = this.props
    if (!prevProps.currentDoorController.fillMarker && fillMarker) this.addDragListeners(fillMarker)
  }

  addDragListeners = marker => {
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragstart', _ => this.setState({ dragging: true })))
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragend', _ => this.setState({ dragging: false })))
  }

  componentWillUnmount = _ => this.markerDragListeners.forEach(listener => listener && listener.remove())

  configure = _ => {
    const { currentDoorController: { id, location, headingCounter }, showAddFirstDoor, map, addDoorAction, updateUiAction } = this.props

    if (showAddFirstDoor) {
      addDoorAction(id, getNewDeviceLocation(map, location, headingCounter))
    }

    updateUiAction({ modal: 'DoorControllerDesigner' })
  }

  doorRow = (door, skipDrag = false) => {
    const { props: { map, updateCurrentEntityAction, classes }, state: { dragging } } = this
    const onClick = _ => {
      updateCurrentEntityAction({ id: door.id, type: 'door' })
      map.panTo(door.location)
    }

    return (
      <div key={'door' + door.id} className={classes.door}>
        <div className={classes.doorIcon} style={{ background: door.color }}>
          <FontAwesomeIcon icon={['far', 'door-open']} style={{ margin: 'auto' }} />
        </div>
        <div style={{ flex: 1 }}>
          <a onClick={onClick}>
            {door.name} <FontAwesomeIcon icon='arrow-right' />
          </a>
          <span className={classes.distanceText}>
            Length:
            <span className={(!skipDrag && dragging) ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>
              { ` ${door.distance}` }
            </span>
          </span>
        </div>
      </div>
    )
  }

  expansionRow = expansion => {
    const { props: { map, updateCurrentEntityAction, isImperial, classes }, state: { dragging } } = this
    const onClick = _ => {
      updateCurrentEntityAction({ id: expansion.id, type: 'doorController' })
      map.panTo(expansion.location)
    }

    return (
      <div key={'expansion' + expansion.id} className={classes.expansionList}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className={classes.doorIcon} style={{ background: expansion.color }}>
            <FontAwesomeIcon icon={['far', 'microchip']} style={{ margin: 'auto' }} />
          </div>
          <div style={{ flex: 1 }}>
            <a onClick={onClick}>
              {expansion.name} <FontAwesomeIcon icon='arrow-right' />
            </a>
            <span className={classes.distanceText}>
              Length:
              <span className={(expansion.hasWire && !expansion.wireIsEthernet && dragging) ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>
                { expansion.hasWire ? ` ${expansion.distance}` : ' —' }
                { expansion.wireIsEthernet && <EthernetExpansionWarning /> }
                { expansion.wireIsEthernet && expansion.showWarning && <WireDistanceWarning isImperial={isImperial} /> }
              </span>
            </span>
          </div>
        </div>
        <div className={classes.doorList} style={{ marginLeft: '.5rem', paddingTop: '.5rem' }}>
          <span>Doors: {expansion.doors.length}</span>
          <span className={classes.distanceText} style={{ paddingBottom: '.5rem' }}>
            Total Length:
            <span>{` ${expansion.totalDistance}`}</span>
          </span>
          <div className={classes.doorWrapper}>
            { expansion.doors.map(door => this.doorRow(door, true)) }
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { currentDoorController, doors, showAddFirstDoor, totalDistance, expansions, totalExpansionDistance, modelCount, wiringPanelOpen,
      doorControllerSummaryPanelOpen, cameraDescriptionPanelOpen, updateUiAction, messageAction, updateEntityAction, destroyDoorControllerAction,
      zoomToDoorControllerAction, updateCurrentEntityAction, checkOfflineLimitsAction, classes } = this.props
    const { id, locked, name, location, color, enclosure, model, isExpansion, parentId } = currentDoorController

    return (
      <div className={classes.details}>
        <div className={classes.topUiControls}>
          <a onClick={_ => isExpansion ? updateCurrentEntityAction({ id: parentId, type: 'doorController' }) : updateUiAction({ sidebarView: 'cameraList' })}>
            <FontAwesomeIcon icon='caret-left' className={classes.goBackButton} />
            {isExpansion ? 'Main Controller' : 'All Project Items'}
          </a>
          <a
            onClick={_ => {
              if (locked) {
                messageAction('generalError', 'Cannot delete locked Door Controller. Right click to unlock.')
              } else if (window.confirm(`Are you sure you want to delete ${name}?`)) {
                destroyDoorControllerAction(id)
              }
            }}
            className={classes.destroyButton}>
            <FontAwesomeIcon icon={['far', 'trash-alt']} />
          </a>
        </div>
        <div className={classes.container}>
          <div title='Name' className={classes.label}>Name</div>
          <input
            type='text'
            onChange={({ target: { value } }) => updateEntityAction('doorControllers', id, { name: value })}
            value={name || ''}
          />
          <div className={classes.actions}>
            <a onClick={_ => zoomToDoorControllerAction(currentDoorController)}>
              <FontAwesomeIcon icon='map-marker-alt' />
            </a>
            <a onClick={_ => updateEntityAction('doorControllers', id, { locked: !locked })}>
              <FontAwesomeIcon icon={locked ? 'lock' : 'unlock'} />
            </a>
          </div>
        </div>
        <ModelName
          model={model}
          modelCount={modelCount}
          updateUiAction={updateUiAction}
          isExpansion={isExpansion}
        />
        <div className={classes.container}>
          <span className={classes.colorLabel}>Color</span>
          <BorderPicker
            currentMarkup={{ id, type: 'doorController', strokeColor: color }}
            updateMarkupAction={(id, action) => updateEntityAction('doorControllers', id, { color: action.strokeColor })}
            cameraOnly
          />
        </div>
        <div className={classes.container}>
          <span className={classes.otherLabel}>Configuration</span>
          <div
            onClick={this.configure}
            className={showAddFirstDoor ? classes.addDoorButton : classes.configureButton}
          >
            { showAddFirstDoor ? 'First, Add a Door' : 'Configure Doors' }
          </div>
        </div>
        <div className={classes.container}>
          <span className={classes.otherLabel}>Enclosure</span>
          <input
            type='text'
            className={classes.input}
            onChange={({ target: { value } }) => updateEntityAction('doorControllers', id, { enclosure: value })}
            value={enclosure || ''}
          />
        </div>
        <DropLengthInput id={id} desktop door containerClass={classes.container} />
        <div className={classes.doorList}>
          <span>
            {model.allows_expansion && 'Direct '}Doors: {doors.length}
          </span>
          <span className={classes.distanceText} style={{ paddingBottom: '.5rem' }}>
            Total Length:
            <span className={this.state.dragging ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>{ ` ${totalDistance}` }</span>
          </span>
          <div className={classes.doorWrapper}>
            { doors.map(door => this.doorRow(door)) }
          </div>
        </div>
        { (model.allows_expansion || expansions.length > 0) &&
          <div className={classes.expansionList}>
            <span>
              Expansion Controllers: {expansions.length}
            </span>
            <span className={classes.distanceText} style={{ paddingBottom: '.5rem' }}>
              Total Length:
              <span className={this.state.dragging ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>{ ` ${totalExpansionDistance}` }</span>
            </span>
          </div>
        }
        { expansions.map(expansion => this.expansionRow(expansion)) }
        { model.allows_expansion &&
          <div className={classes.expansionButtonWrapper}>
            <div onClick={_ => checkOfflineLimitsAction('modal', ['ExpansionControllerModelSelector'])} className={classes.expansionButton}>
              Add Expansion Controller
            </div>
          </div>
        }
        { (!isExpansion || (isExpansion && model.connection === 'Ethernet')) &&
          <Panel
            active={wiringPanelOpen}
            component={<WiringPanel startDevice={currentDoorController} startDeviceType='doorController' />}
            title='Cabling to Network Closet'
            updateUiAction={updateUiAction}
            uiKey='wiringPanelOpen'
          />
        }
        <Panel
          active={doorControllerSummaryPanelOpen}
          component={<ConfigurationSummary isController />}
          title='Device Summary'
          updateUiAction={updateUiAction}
          uiKey='doorControllerSummaryPanelOpen'
        />
        <Panel
          active={cameraDescriptionPanelOpen}
          component={<DeviceDescription currentDevice={currentDoorController} type='doorController' />}
          title='Location, Description, Notes'
          updateUiAction={updateUiAction}
          uiKey='cameraDescriptionPanelOpen'
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateUiAction, updateEntityAction, destroyDoorControllerAction, updateCurrentEntityAction,
  addDoorAction, zoomToDoorControllerAction, checkOfflineLimitsAction, messageAction })(injectSheet(style)(DoorControllerDetails))



// WEBPACK FOOTER //
// ./src/components/DoorControllerDetails/index.js